/**
 * @voltxampere/math — Dense LU decomposition with partial pivoting
 *
 * SPECS §3.11 names KLU-style sparse LU as the production target.
 * Sprint 4 ships a correctness-first dense path: PA = LU with row
 * pivoting, then forward-/back-substitution. Sprint 10+ swaps in a
 * sparse implementation without changing the public surface.
 *
 * The factor `L` is unit lower triangular (diagonal = 1) and `U` is
 * upper triangular. `P` is stored as a permutation vector — index `i`
 * tells which original row landed at row `i` after pivoting.
 *
 * Singular pivots throw {@link SingularMatrixError}. We never silently
 * "regularise" by adding a fake gmin (that's a Sprint 5 concern at
 * the solver layer; the math primitive must report the failure).
 *
 * Indexed access uses `?? 0` to satisfy `noUncheckedIndexedAccess`
 * without resorting to non-null assertions (banned by the project
 * lint config). For an in-bounds read into `Float64Array` the
 * fallback is never observed at runtime.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError, SingularMatrixError } from "@voltxampere/shared";
import { createMatrix, matrixClone } from "../dense/matrix.js";
function assertSquare(a, op) {
    if (a.rows !== a.cols) {
        throw new ContractViolationError(`${op}: matrix must be square (got ${String(a.rows)}x${String(a.cols)})`);
    }
}
/**
 * Numerical zero threshold. Pivots smaller than this in absolute
 * value are treated as singular. Tighter than `Number.EPSILON` would
 * be too aggressive given accumulated round-off after several
 * elimination steps.
 */
const PIVOT_THRESHOLD = 1e-14;
/**
 * Factor `a` into `P · a = L · U` using partial (row) pivoting. The
 * input is not modified — work happens on a clone.
 */
export function luDecompose(a) {
    assertSquare(a, "luDecompose");
    const n = a.rows;
    const work = matrixClone(a);
    const P = new Uint32Array(n);
    for (let i = 0; i < n; i++)
        P[i] = i;
    for (let k = 0; k < n; k++) {
        // Find the pivot row: index of max |work[i,k]| for i ≥ k.
        let pivotRow = k;
        let pivotVal = Math.abs(work.data[k * n + k] ?? 0);
        for (let i = k + 1; i < n; i++) {
            const candidate = Math.abs(work.data[i * n + k] ?? 0);
            if (candidate > pivotVal) {
                pivotVal = candidate;
                pivotRow = i;
            }
        }
        if (pivotVal < PIVOT_THRESHOLD) {
            throw new SingularMatrixError(`luDecompose: zero pivot at column ${String(k)} (|pivot|=${String(pivotVal)})`);
        }
        // Swap rows k and pivotRow in `work` (and update `P`).
        if (pivotRow !== k) {
            for (let j = 0; j < n; j++) {
                const tmp = work.data[k * n + j] ?? 0;
                work.data[k * n + j] = work.data[pivotRow * n + j] ?? 0;
                work.data[pivotRow * n + j] = tmp;
            }
            const tmpP = P[k] ?? 0;
            P[k] = P[pivotRow] ?? 0;
            P[pivotRow] = tmpP;
        }
        // Eliminate below the pivot.
        const pivot = work.data[k * n + k] ?? 0;
        for (let i = k + 1; i < n; i++) {
            const factor = (work.data[i * n + k] ?? 0) / pivot;
            // Store the multiplier in the L position (below the diagonal of `work`).
            work.data[i * n + k] = factor;
            for (let j = k + 1; j < n; j++) {
                work.data[i * n + j] = (work.data[i * n + j] ?? 0) - factor * (work.data[k * n + j] ?? 0);
            }
        }
    }
    // Split `work` into L (unit lower) and U (upper).
    const L = createMatrix(n, n);
    const U = createMatrix(n, n);
    for (let i = 0; i < n; i++) {
        L.data[i * n + i] = 1;
        for (let j = 0; j < n; j++) {
            const v = work.data[i * n + j] ?? 0;
            if (j < i)
                L.data[i * n + j] = v;
            else
                U.data[i * n + j] = v;
        }
    }
    return { L, U, P };
}
/** Reorder `b` so that `b'[i] = b[P[i]]`. */
function applyPermutation(b, P) {
    const out = new Float64Array(b.length);
    for (let i = 0; i < P.length; i++) {
        const src = P[i] ?? 0;
        out[i] = b[src] ?? 0;
    }
    return out;
}
/**
 * Solve `L·U·x = P·b` given a previously-computed factorisation.
 * Returns a fresh `Float64Array`.
 */
export function luSolve(lu, b) {
    const n = lu.L.rows;
    if (b.length !== n) {
        throw new ContractViolationError(`luSolve: rhs length ${String(b.length)} ≠ matrix rank ${String(n)}`);
    }
    for (let i = 0; i < b.length; i++) {
        const bi = b[i] ?? 0;
        if (!Number.isFinite(bi)) {
            throw new ContractViolationError(`luSolve: rhs[${String(i)}] must be finite, got ${String(bi)}`);
        }
    }
    // Forward solve L·y = P·b.
    const pb = applyPermutation(b, lu.P);
    const y = new Float64Array(n);
    for (let i = 0; i < n; i++) {
        let s = pb[i] ?? 0;
        for (let j = 0; j < i; j++)
            s -= (lu.L.data[i * n + j] ?? 0) * (y[j] ?? 0);
        y[i] = s; // L diagonal is 1
    }
    // Back solve U·x = y.
    const x = new Float64Array(n);
    for (let i = n - 1; i >= 0; i--) {
        let s = y[i] ?? 0;
        for (let j = i + 1; j < n; j++)
            s -= (lu.U.data[i * n + j] ?? 0) * (x[j] ?? 0);
        const diag = lu.U.data[i * n + i] ?? 0;
        if (Math.abs(diag) < PIVOT_THRESHOLD) {
            throw new SingularMatrixError(`luSolve: zero diagonal in U at row ${String(i)}`);
        }
        x[i] = s / diag;
    }
    return x;
}
/** Convenience: factor and solve in one call. */
export function solve(a, b) {
    return luSolve(luDecompose(a), b);
}
//# sourceMappingURL=dense-lu.js.map